// Named sidebar data providers for the Support app.
//
// Both exist because their rows ARE data: the teams you belong to, and per-status
// user counts that have to be derived by joining the member directory with the
// invite list and filtering on the SUPPORT app grant. Neither is expressible as
// a declarative `{ api, map }`.

import { useMemo } from 'react';
import { useMyTeams } from '@clarix/services-support';
import { useWorkspaceInviteList, useWorkspaceMemberDirectory } from '@clarix/services-users';
import type { RowConfig, SidebarProviderResult } from '@clarix/shared-types';

/**
 * The teams the caller is on. These ARE the nav for /support/teams: the page
 * renders only the selected team's reading pane, exactly like Settings renders
 * only the active tab. Selection travels in the URL (`?team=<id>`) so a deep
 * link, the panel and the page can never disagree.
 */
export function useSupportTeamsProvider(): SidebarProviderResult {
  const { data, isLoading, isError } = useMyTeams();

  const rows = useMemo<RowConfig[]>(
    () =>
      (data ?? []).map((team) => {
        const isLead = team.my.role === 'lead';
        // `teammates` excludes the caller — count them back in.
        const members = team.teammates.length + 1;
        return {
          id: String(team.team_id),
          label: team.team_name,
          icon: 'Users',
          toParam: { pathname: '/support/teams', param: 'team', value: String(team.team_id) },
          pills: [
            {
              label: isLead ? 'LEAD' : 'MEMBER',
              className: isLead ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600',
              title: `${members} member${members === 1 ? '' : 's'}`,
            },
          ],
        };
      }),
    [data],
  );

  return { rows, isLoading, isError, emptyText: 'You are not in any team yet' };
}

/**
 * Per-status user shortcuts, counted exactly like the Support Users page's tabs:
 * a member counts only when their `app_access` carries a SUPPORT grant (grant
 * ACTIVE + membership ACTIVE → Active, else Deactivated); a PENDING invite that
 * includes SUPPORT counts as Invited. "All" is the sum of the three.
 */
export function useSupportUsersProvider(): SidebarProviderResult {
  const membersQuery = useWorkspaceMemberDirectory();
  const invitesQuery = useWorkspaceInviteList();

  const counts = useMemo(() => {
    let active = 0;
    let deactive = 0;
    let invited = 0;
    for (const m of membersQuery.data ?? []) {
      const access = (m.app_access ?? []).find((a) => a.code === 'SUPPORT');
      if (!access) continue;
      if (access.status === 'ACTIVE' && m.status === 'ACTIVE') active += 1;
      else deactive += 1;
    }
    for (const invite of invitesQuery.data ?? []) {
      if (invite.status !== 'PENDING') continue;
      if ((invite.apps_json ?? []).some((a) => a.code === 'SUPPORT')) invited += 1;
    }
    return { all: active + deactive + invited, active, deactive };
  }, [membersQuery.data, invitesQuery.data]);

  return {
    rows: [
      {
        id: 'all',
        label: 'All Users',
        icon: 'Users',
        // The page treats a MISSING `?status=` as the All tab, so this row keeps
        // the bare path rather than inventing `?status=all` the page can't read.
        to: '/support/users',
        activeExtra: { paramAbsent: ['status'] },
        badge: { kind: 'count', tone: 'gray', value: counts.all },
      },
      {
        id: 'active',
        label: 'Active',
        icon: 'UserCheck',
        iconTone: 'emerald',
        to: '/support/users?status=active',
        badge: { kind: 'count', tone: 'emerald', value: counts.active },
      },
      {
        id: 'deactivated',
        label: 'Deactivated',
        icon: 'UserX',
        to: '/support/users?status=deactivated',
        badge: { kind: 'count', tone: 'gray', value: counts.deactive },
      },
    ],
    isLoading: membersQuery.isLoading || invitesQuery.isLoading,
    isError: membersQuery.isError,
  };
}
